import { CSSProperties, useEffect, useRef, useState } from 'react';
import { useReduxState } from 'hooks';
import isEmail from 'validator/lib/isEmail';
import { useField } from 'informed';
import api from 'api';
import { User } from 'components/common';
import { SelectInput } from 'components/common/form/common';

const findExtraSeparatorsAtStartRegex = /^[<.,;'\\\s]*/;
const findExtraSeparatorsAtEndRegex = /[>.,;'\\\s]*$/;

type UsernamesInputValue = Record<'error' | 'label' | 'value', string>[];

export interface UsernamesProps {
  countNewUsers?: number;
  disabled?: boolean;
  disableCreatingNewAccounts?: boolean;
  disableOptions?: boolean;
  excludeUsersWithEmail?: boolean;
  existingUsers?: number[];
  existingUsersOnly?: boolean;
  field: string;
  id?: string;
  isRequired?: boolean;
  onChange?: (value: any) => void;
  onUserChange?: (value: any) => void;
  style?: CSSProperties;
}

const Usernames = (props: UsernamesProps) => {
  const {
    messages,
    serverConfig: { enableUserSearch },
    theme,
    user
  } = useReduxState();

  const getUsersTimeout = useRef<NodeJS.Timeout>(undefined);

  const { fieldState, fieldApi, ref, userProps } = useField<string[]>({
    ...props
  });

  const [inputValue, setInputValue] = useState('');
  const [usernames, setUsernames] = useState<UsernamesInputValue>(
    fieldState.value?.length
      ? fieldState.value.map((value) => ({ error: '', label: value, value }))
      : []
  );

  const {
    countNewUsers = 0,
    disabled,
    disableCreatingNewAccounts,
    disableOptions,
    excludeUsersWithEmail,
    existingUsers,
    existingUsersOnly = false,
    id,
    isRequired,
    onChange,
    onUserChange,
    style,
    ...params
  } = userProps;

  useEffect(() => {
    return () => clearTimeout(getUsersTimeout.current);
  }, []);

  const getUserOptions = (value: string) => {
    const query = value.trim();

    if (!query) {
      return Promise.resolve([]);
    }

    let userIds = new Set<number>();
    if (existingUsers) userIds = new Set(existingUsers);

    clearTimeout(getUsersTimeout.current);
    return new Promise((resolve) => {
      getUsersTimeout.current = setTimeout(async () => {
        try {
          const response = await api.get(
            `/admin/users?search=${query}&role=user`
          );
          if (response.data && response.data.success) {
            let users = response.data.users.map((user) => ({
              hasEmail: Boolean(user.email),
              label: <User user={user} />,
              value: user.username,
              id: user.id
            }));
            if (excludeUsersWithEmail) {
              users = users.filter((user) => !user.hasEmail);
            }
            if (userIds.size) {
              users = users.filter((user) =>
                existingUsersOnly ? userIds.has(user.id) : !userIds.has(user.id)
              );
            }

            resolve(users);
          } else resolve([]);
        } catch (err) {
          resolve([]);
        }
      }, 500);
    });
  };

  const getValidUsernames = (usernames: UsernamesInputValue) =>
    usernames.filter(({ error }) => !error).map(({ value }) => value);

  const applyUsernames = (nextUsernames: UsernamesInputValue) => {
    setUsernames(nextUsernames);

    const validUsernames = getValidUsernames(nextUsernames);

    if (isRequired && !nextUsernames.length) {
      fieldApi.setError(messages.commonFieldRequired);
    } else if (nextUsernames.length > 0 && validUsernames.length === 0) {
      // tags exist but none are valid
      fieldApi.setError(messages.authInvalidUsername);
    } else {
      fieldApi.setError(undefined);
    }

    fieldApi.setValue(validUsernames);
    fieldApi.setTouched(true);

    return validUsernames;
  };

  const handleChange = (arr: UsernamesInputValue) => {
    const validUsernames = applyUsernames(arr);

    if (onChange) onChange(validUsernames);
  };

  const parseUsernames = (
    search: string,
    currentUsernames: UsernamesInputValue
  ) => {
    const parsedUsernames: UsernamesInputValue = [];
    let remainingSearch = search;

    const matches = search.split(/[,;\s]+/);
    if (matches.length > 1) {
      const addedUsernames = new Set(
        currentUsernames.map(({ value }) => value)
      );

      matches.forEach((username: string) => {
        const lowerCaseUsername = username.toLowerCase().trim();
        const valueInField = { label: username, value: lowerCaseUsername };

        if (!lowerCaseUsername || addedUsernames.has(lowerCaseUsername)) {
          return;
        } else if (isEmail(lowerCaseUsername)) {
          parsedUsernames.push({
            ...valueInField,
            error: messages.authInvalidEmailAsUsername
          });
        } else parsedUsernames.push({ ...valueInField, error: '' });

        remainingSearch = remainingSearch
          .replace(username, '')
          .replace(findExtraSeparatorsAtStartRegex, '')
          .replace(findExtraSeparatorsAtEndRegex, '');
      });
    }

    return {
      parsedUsernames,
      remainingSearch
    };
  };

  const handleInputChange = (currentValue: string, { action }) => {
    if (action === 'menu-close') return;

    let search = currentValue.trimStart();
    if (action === 'input-blur') {
      search = inputValue + ' '; // append separator to force parsing
    }

    const { parsedUsernames, remainingSearch } = parseUsernames(
      search,
      usernames
    );
    setInputValue(remainingSearch);

    if (!parsedUsernames.length) return;

    const validUsernames = applyUsernames([...usernames, ...parsedUsernames]);

    if (!disableCreatingNewAccounts && onUserChange) {
      onUserChange(validUsernames);
    }

    if (disableCreatingNewAccounts) {
      validateExistence(parsedUsernames.map((u) => u.value));
    } // if account creation is allowed, existence is checked by the parent when creating new accounts
  };

  const validateExistence = async (usernamesToCheck: string[]) => {
    const existing = await getExistingUsernames(usernamesToCheck);

    setUsernames((prev) => {
      const next = prev.map((tag) =>
        usernamesToCheck.includes(tag.value) &&
        !existing.includes(tag.value) &&
        !tag.error
          ? { ...tag, error: messages.authInvalidUsername }
          : tag
      );

      const validUsernames = getValidUsernames(next);

      if (next.length > 0 && validUsernames.length === 0) {
        fieldApi.setError(messages.authInvalidUsername);
      }

      fieldApi.setValue(validUsernames);
      return next;
    });
  };

  const handleKeyDown = (ev: React.KeyboardEvent) => {
    // prevent enter from submitting form
    if (ev.key === 'Enter') {
      // Only prevent default if there's no active option in the dropdown
      const hasActiveOption = ref.current?.state?.focusedOption;
      if (!hasActiveOption) {
        ev.preventDefault();
        // hack to check input value
        if (!ref.current.state.loadedOptions?.length) {
          handleInputChange('', {
            action: 'input-blur'
          });
        }
      }
    }
  };

  const numberOfInvalidUsers =
    (usernames.length || 0) - (fieldState.value?.length || 0);

  return (
    <div style={style} onKeyDown={handleKeyDown}>
      <SelectInput
        hideDropdownIndicator={true}
        id={id}
        inputId={`${id}-input`}
        inputValue={inputValue}
        isClearable={false}
        isDisabled={disabled}
        isMulti={true}
        isSearchable={true}
        loadingMessage={() => null}
        menuIsOpen={true}
        menuWidth="360px"
        noOptionsMessage={() => null}
        onChange={handleChange}
        onInputChange={handleInputChange}
        placeholder="user1, user2, ..."
        selectRef={ref}
        value={usernames || null}
        {...(user?.roles?.includes('superadmin') ||
        (enableUserSearch && !disableOptions)
          ? { async: true, loadOptions: getUserOptions }
          : {})}
        {...params}
      />

      {fieldState.error ? (
        <div className="error">{fieldState.error}</div>
      ) : usernames.length ? (
        <div className="username-count">
          {`${fieldState.value?.length || 0} ${
            messages.settingsAddUsersUsernames
          }`}
          {numberOfInvalidUsers
            ? `, ${numberOfInvalidUsers} ${messages.usersImportInvalid.toLowerCase()}`
            : null}
          {countNewUsers > 0
            ? `, ${countNewUsers} ${messages.commonNewUsers.toLowerCase()}`
            : null}
        </div>
      ) : null}

      <style jsx>{`
        .error {
          color: ${theme.dangerColor};
          font-size: 1.3rem;
          padding: 3px;
        }
        .username-count {
          color: ${theme.textColorLighter};
          font-size: 1.3rem;
          padding: 3px;
        }
      `}</style>
    </div>
  );
};

export default Usernames;

const getExistingUsernames = async (usernames: string[]) => {
  try {
    const response = await api.post('/admin/users-existing', { usernames });
    return usernames.filter((user) => response.data.usersByUsername[user]);
  } catch (_error) {
    return [];
  }
};
